/**
 * Phase 32 Plan 32-2 — engineering-metrics TanStack Query hooks.
 *
 * Query keys are namespaced under ['engineering-metrics', ...] so a future
 * mutation hook (e.g. value-tag write in Plan 32-3) can invalidate scoped
 * caches without nuking unrelated server state.
 *
 * `refetchOnWindowFocus: false` — the daily cron updates the underlying
 * data once per day; aggressive refetching on focus would just hit the
 * cache without producing fresher data.
 */
import { useQuery } from '@tanstack/react-query';
import {
  fetchAutomationShare,
  fetchEngineerSeries,
  fetchLeaderboard,
  fetchUnmappedCommits,
  type Window,
} from '../services/api/engineeringMetrics';

export function useLeaderboard(input: { window: Window; projectId?: number }) {
  return useQuery({
    queryKey: ['engineering-metrics', 'leaderboard', input.window, input.projectId ?? null],
    queryFn: () => fetchLeaderboard(input),
    refetchOnWindowFocus: false,
  });
}

export function useEngineerSeries(
  userId: number | undefined,
  input: { window: Window; projectId?: number },
) {
  const enabled = userId !== undefined && Number.isFinite(userId) && userId > 0;
  return useQuery({
    queryKey: [
      'engineering-metrics',
      'engineer',
      userId ?? null,
      input.window,
      input.projectId ?? null,
    ],
    queryFn: () => fetchEngineerSeries(userId as number, input),
    enabled,
    refetchOnWindowFocus: false,
  });
}

export function useAutomationShare(window: Window) {
  return useQuery({
    queryKey: ['engineering-metrics', 'automation-share', window],
    queryFn: () => fetchAutomationShare(window),
    refetchOnWindowFocus: false,
  });
}

export function useUnmappedCommits() {
  return useQuery({
    queryKey: ['engineering-metrics', 'unmapped'],
    queryFn: () => fetchUnmappedCommits(),
    refetchOnWindowFocus: false,
  });
}
